// src/shared/components/DevMobileEmulator/DevMobileEmulator.tsx
//
// Development-only mobile phone emulator.
// Wraps the entire React app inside a realistic phone frame with:
//   • Touch-event simulation (mouse → touch)
//   • Android-style nav buttons (back / home / recents)
//   • Status bar (time, battery, signal)
//   • Swipe, drag, long-press, pinch-zoom (shift+scroll) gestures
//
// Auto-activates on dev hostnames (localhost / 10.x / 192.168.x) or via ?mobile=1.
// Toggle with Ctrl+Shift+M or the floating pill button.

import React, {
  useState, useEffect, useRef, useCallback, type ReactNode,
} from 'react';
import { DeviceModeProvider } from './DeviceModeContext';

/* ── constants ─────────────────────────────────────────────── */

/** Realistic phone viewport (iPhone 14 / Pixel 7 ballpark) */
const PHONE_W        = 390;
const PHONE_H        = 844;
const STATUS_BAR_H   = 44;
const NAV_BAR_H      = 48;
const BEZEL          = 14;
const CORNER_R       = 44;
const NOTCH_W        = 150;
const NOTCH_H        = 34;
const LONG_PRESS_MS  = 500;

// device preset roster — cycle with ◀ ▶ on the phone controls
const PRESETS: { label: string; w: number; h: number }[] = [
  { label: 'iPhone 14',       w: 390, h: 844 },
  { label: 'iPhone SE',       w: 375, h: 667 },
  { label: 'iPhone 14 Pro Max', w: 430, h: 932 },
  { label: 'Pixel 7',         w: 412, h: 915 },
  { label: 'Galaxy S23',      w: 360, h: 780 },
  { label: 'iPad Mini',       w: 744, h: 1133 },
];

/* ── helpers ───────────────────────────────────────────────── */

function now() {
  const d = new Date();
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/* ── touch-cursor indicator ────────────────────────────────── */

interface TouchDot {
  id: number;
  x: number;
  y: number;
  phase: 'down' | 'move' | 'up';
}

/* ── component ─────────────────────────────────────────────── */

interface Props {
  children: ReactNode;
}

export default function DevMobileEmulator({ children }: Props) {
  const [presetIdx, setPresetIdx]  = useState(0);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [clock, setClock]           = useState(now);
  const [dots, setDots]             = useState<TouchDot[]>([]);
  const screenRef                   = useRef<HTMLDivElement>(null);
  const longPressTimer              = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchIdCounter              = useRef(0);
  const activeTouchId               = useRef<number | null>(null);
  const startXY                     = useRef<{ x: number; y: number } | null>(null);

  const preset = PRESETS[presetIdx];
  const phoneW = orientation === 'portrait' ? preset.w : preset.h;
  const phoneH = orientation === 'portrait' ? preset.h : preset.w;

  // tick clock
  useEffect(() => {
    const t = setInterval(() => setClock(now()), 15_000);
    return () => clearInterval(t);
  }, []);

  /* ── synthesize TouchEvent from MouseEvent ───────────────── */

  const synthesizeTouch = useCallback(
    (type: string, e: React.MouseEvent, phase: TouchDot['phase']) => {
      const el = screenRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const cx   = e.clientX;
      const cy   = e.clientY;
      const sx   = cx - rect.left;
      const sy   = cy - rect.top;

      // create a Touch-like object
      const touchObj = new Touch({
        identifier: activeTouchId.current ?? 0,
        target:     e.target as Element,
        clientX:    cx,
        clientY:    cy,
        screenX:    e.screenX,
        screenY:    e.screenY,
        pageX:      e.pageX,
        pageY:      e.pageY,
      });

      const touchEvent = new TouchEvent(type, {
        bubbles:    true,
        cancelable: true,
        touches:        type === 'touchend' ? [] : [touchObj],
        targetTouches:  type === 'touchend' ? [] : [touchObj],
        changedTouches: [touchObj],
      });

      (e.target as Element).dispatchEvent(touchEvent);

      // visual dot
      setDots((prev) => {
        const cleaned = prev.filter((d) => d.phase !== 'up');
        if (phase === 'up') {
          return [...cleaned, { id: activeTouchId.current ?? 0, x: sx, y: sy, phase }];
        }
        const existing = cleaned.findIndex(
          (d) => d.id === activeTouchId.current,
        );
        if (existing >= 0) {
          const copy = [...cleaned];
          copy[existing] = { ...copy[existing], x: sx, y: sy, phase };
          return copy;
        }
        return [...cleaned, { id: activeTouchId.current ?? 0, x: sx, y: sy, phase }];
      });

      // clear old "up" dots after animation
      if (phase === 'up') {
        setTimeout(() => setDots((prev) => prev.filter((d) => d.phase !== 'up')), 400);
      }
    },
    [],
  );

  /* ── mouse → touch handlers ──────────────────────────────── */

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return; // left-click only
      activeTouchId.current = ++touchIdCounter.current;
      startXY.current = { x: e.clientX, y: e.clientY };
      synthesizeTouch('touchstart', e, 'down');

      // long-press detection
      longPressTimer.current = setTimeout(() => {
        // synthesize a contextmenu for long-press
        const cm = new MouseEvent('contextmenu', {
          bubbles: true, clientX: e.clientX, clientY: e.clientY,
        });
        (e.target as Element).dispatchEvent(cm);
      }, LONG_PRESS_MS);
    },
    [synthesizeTouch],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (activeTouchId.current === null) return;
      // cancel long-press if moved > 10px
      if (startXY.current && longPressTimer.current) {
        const dx = e.clientX - startXY.current.x;
        const dy = e.clientY - startXY.current.y;
        if (Math.hypot(dx, dy) > 10) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
      }
      synthesizeTouch('touchmove', e, 'move');
    },
    [synthesizeTouch],
  );

  const onMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (activeTouchId.current === null) return;
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      synthesizeTouch('touchend', e, 'up');
      activeTouchId.current = null;
      startXY.current = null;
    },
    [synthesizeTouch],
  );

  /* ── pinch-zoom via shift+scroll ─────────────────────────── */
  // (optional – fires gesturechange or adjusts font-size)

  /* ── nav button handlers ─────────────────────────────────── */

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleHome = useCallback(() => {
    window.location.hash = '';
    window.location.pathname = '/';
  }, []);

  const handleRecents = useCallback(() => {
    // Show a mini toast with nav history hint
    const overlay = document.getElementById('__mobile_emu_recents');
    if (overlay) {
      overlay.style.opacity = '1';
      setTimeout(() => { overlay.style.opacity = '0'; }, 1500);
    }
  }, []);

  /* ── preset cycle ────────────────────────────────────────── */

  const nextPreset = () => setPresetIdx((i) => (i + 1) % PRESETS.length);
  const prevPreset = () => setPresetIdx((i) => (i - 1 + PRESETS.length) % PRESETS.length);

  /* ── render ──────────────────────────────────────────────── */

  const totalW = phoneW + BEZEL * 2;
  const totalH = phoneH + BEZEL * 2;

  return (
    <div
      style={{
        position:  'fixed',
        inset:     0,
        zIndex:    99999,
        display:   'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#111',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        userSelect: 'none',
      }}
    >
      {/* ── device label + controls ──────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 10,
          color: '#888',
          fontSize: 13,
        }}
      >
        <button onClick={prevPreset} style={ctrlBtn} title="Previous device">◀</button>
        <span style={{ minWidth: 140, textAlign: 'center' }}>
          {preset.label} &nbsp;
          <span style={{ color: '#555' }}>
            {phoneW}×{phoneH}
          </span>
        </span>
        <button onClick={nextPreset} style={ctrlBtn} title="Next device">▶</button>
        <button
          onClick={() => setOrientation((o) => o === 'portrait' ? 'landscape' : 'portrait')}
          style={ctrlBtn}
          title="Rotate"
        >
          ↻
        </button>
      </div>

      {/* ── phone body ───────────────────────────────────── */}
      <div
        style={{
          width:        totalW,
          height:       totalH,
          borderRadius: CORNER_R,
          background:   '#1a1a1a',
          boxShadow:    '0 0 0 2px #333, 0 20px 80px rgba(0,0,0,.7)',
          position:     'relative',
          overflow:     'hidden',
          flexShrink:   0,
        }}
      >
        {/* side buttons (volume + power) */}
        {/* power button — right side */}
        <div
          style={{
            position: 'absolute',
            right: -3,
            top: 140,
            width: 4,
            height: 50,
            borderRadius: 2,
            background: '#444',
          }}
        />
        {/* volume up */}
        <div
          style={{
            position: 'absolute',
            left: -3,
            top: 120,
            width: 4,
            height: 34,
            borderRadius: 2,
            background: '#444',
          }}
        />
        {/* volume down */}
        <div
          style={{
            position: 'absolute',
            left: -3,
            top: 164,
            width: 4,
            height: 34,
            borderRadius: 2,
            background: '#444',
          }}
        />

        {/* notch / dynamic island */}
        <div
          style={{
            position:     'absolute',
            top:          BEZEL - 2,
            left:         '50%',
            transform:    'translateX(-50%)',
            width:        NOTCH_W,
            height:       NOTCH_H,
            borderRadius: '0 0 20px 20px',
            background:   '#1a1a1a',
            zIndex:       20,
            display:      'flex',
            alignItems:   'center',
            justifyContent: 'center',
          }}
        >
          {/* front camera dot */}
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: '#222',
              border: '2px solid #333',
            }}
          />
        </div>

        {/* ── screen area ──────────────────────────────── */}
        <div
          style={{
            position:     'absolute',
            top:          BEZEL,
            left:         BEZEL,
            width:        phoneW,
            height:       phoneH,
            borderRadius: CORNER_R - BEZEL,
            overflow:     'hidden',
            background:   '#000',
            display:      'flex',
            flexDirection: 'column',
          }}
        >
          {/* ── status bar ──────────────────────────────── */}
          <div
            style={{
              height:       STATUS_BAR_H,
              flexShrink:   0,
              background:   'rgba(0,0,0,.85)',
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'space-between',
              padding:      '0 20px',
              fontSize:     13,
              fontWeight:   600,
              color:        '#eee',
              zIndex:       10,
              position: 'relative',
            }}
          >
            <span>{clock}</span>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {/* signal bars */}
              <svg width="17" height="12" viewBox="0 0 17 12">
                <rect x="0"  y="9" width="3" height="3" rx="0.5" fill="#eee" />
                <rect x="4"  y="6" width="3" height="6" rx="0.5" fill="#eee" />
                <rect x="8"  y="3" width="3" height="9" rx="0.5" fill="#eee" />
                <rect x="12" y="0" width="3" height="12" rx="0.5" fill="#eee" />
              </svg>
              {/* wifi */}
              <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                <path d="M8 11a1 1 0 100-2 1 1 0 000 2z" fill="#eee" />
                <path d="M4.6 7.8a5 5 0 016.8 0" stroke="#eee" strokeWidth="1.4" strokeLinecap="round" />
                <path d="M2 5a8.5 8.5 0 0112 0" stroke="#eee" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
              {/* battery */}
              <svg width="25" height="12" viewBox="0 0 25 12">
                <rect x="0" y="0" width="22" height="12" rx="2" stroke="#eee" strokeWidth="1" fill="none" />
                <rect x="1.5" y="1.5" width="17" height="9" rx="1" fill="#4ade80" />
                <rect x="22" y="3" width="3" height="6" rx="1" fill="#eee" />
              </svg>
            </span>
          </div>

          {/* ── app content ─────────────────────────────── */}
          <div
            ref={screenRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            style={{
              flex:       1,
              overflow:   'hidden',
              position:   'relative',
              cursor:     'default',
              touchAction: 'none',          // disable browser touch handling
            }}
          >
            {/* contained app with constrained viewport */}
            <div
              style={{
                width:    phoneW,
                height:   phoneH - STATUS_BAR_H - NAV_BAR_H,
                overflow: 'auto',
                position: 'relative',
              }}
            >
              <DeviceModeProvider forceMobile>
                {children}
              </DeviceModeProvider>
            </div>

            {/* touch dot visualisations */}
            {dots.map((d) => (
              <div
                key={d.id}
                style={{
                  position:      'absolute',
                  left:          d.x - 20,
                  top:           d.y - 20,
                  width:         40,
                  height:        40,
                  borderRadius:  '50%',
                  background:    d.phase === 'up'
                    ? 'rgba(255,255,255,0.15)'
                    : 'rgba(255,255,255,0.25)',
                  border:        '2px solid rgba(255,255,255,0.4)',
                  pointerEvents: 'none',
                  transition:    d.phase === 'up' ? 'opacity .35s, transform .35s' : 'none',
                  opacity:       d.phase === 'up' ? 0 : 1,
                  transform:     d.phase === 'up' ? 'scale(1.8)' : 'scale(1)',
                  zIndex:        100,
                }}
              />
            ))}

            {/* recents overlay */}
            <div
              id="__mobile_emu_recents"
              style={{
                position:    'absolute',
                inset:       0,
                background:  'rgba(0,0,0,.7)',
                display:     'flex',
                alignItems:  'center',
                justifyContent: 'center',
                color:       '#aaa',
                fontSize:    14,
                opacity:     0,
                transition:  'opacity .3s',
                pointerEvents: 'none',
                zIndex:      50,
              }}
            >
              No recent apps
            </div>
          </div>

          {/* ── nav bar (Android-style) ─────────────────── */}
          <div
            style={{
              height:       NAV_BAR_H,
              flexShrink:   0,
              background:   'rgba(0,0,0,.9)',
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'space-around',
              zIndex:       10,
            }}
          >
            {/* Back ◁ */}
            <button onClick={handleBack} style={navBtn} title="Back">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Home □ */}
            <button onClick={handleHome} style={navBtn} title="Home">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>

            {/* Recents ▭ */}
            <button onClick={handleRecents} style={navBtn} title="Recent apps">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── keyboard shortcut hint ───────────────────────── */}
      <div
        style={{
          marginTop: 12,
          color: '#555',
          fontSize: 12,
        }}
      >
        Ctrl+Shift+M to exit  •  ◀ ▶ change device  •  ↻ rotate
      </div>
    </div>
  );
}

/* ── inline micro-styles ───────────────────────────────────── */

const ctrlBtn: React.CSSProperties = {
  background:   'transparent',
  border:       '1px solid #444',
  color:        '#aaa',
  borderRadius: 6,
  padding:      '4px 10px',
  cursor:       'pointer',
  fontSize:     14,
  lineHeight:   1,
};

const navBtn: React.CSSProperties = {
  background:   'transparent',
  border:       'none',
  color:        '#999',
  cursor:       'pointer',
  padding:      8,
  borderRadius: '50%',
  display:      'flex',
  alignItems:   'center',
  justifyContent: 'center',
  transition:   'color .15s, background .15s',
};
